/** @format */

import React, { useMemo } from 'react'
import styled from 'styled-components'
import { _$, Skeleton } from '@king-design/react'
import { useAsync } from 'react-use'
import { getOverviews } from 'src/api/ApiAIObservability'
import { tokenValueFormat } from '../../constant'
import variable from 'src/static/styles/var'
import { numberFormat } from 'src/utils/common'

interface OverviewProps {
  className?: string
  serviceName?: string
  startTime: number
  endTime: number
  region?: string
}

interface MetricCardProps {
  label: string
  value: React.ReactNode
  hasError?: boolean
}

export const MetricCard: React.FC<MetricCardProps> = ({ label, value, hasError }) => (
  <article className={`metric-card ${hasError ? 'metric-card-error' : ''}`} role="listitem">
    <div className="metric-card__header">
      <span className="metric-card__label">{label}</span>
    </div>
    <span className="metric-card__value">{value}</span>
  </article>
)

const Overview: React.FC<OverviewProps> = ({
  className,
  serviceName,
  startTime,
  endTime,
  region
}) => {
  const { value: overviewData, loading } = useAsync(async () => {
    if (startTime && endTime) {
      const result = await getOverviews({
        ServiceName: serviceName || '',
        StartTime: startTime,
        EndTime: endTime,
        Region: region
      })
      return result
    }
  }, [serviceName, startTime, endTime, region])

  const errorCount = Number(overviewData?.ErrorCount)
  const hasErrorCount = !Number.isNaN(errorCount) && errorCount > 0

  const tokenUsage = useMemo(() => {
    const token = tokenValueFormat(overviewData?.TokenTotalUsage)
    if (token.includes('k') || token.includes('m')) {
      const [value, unit] = token.split(/(k|m)/)
      return {
        value: value,
        unit: unit
      }
    }
    return { value: token }
  }, [overviewData?.TokenTotalUsage])

  return (
    <div className={className}>
      <section aria-label={_$('总览')}>
        {loading ? (
          <Skeleton loading={true} />
        ) : (
          <div className="overview-metrics__grid" role="list">
            {!serviceName && overviewData?.ServiceCount !== undefined ? (
              <MetricCard label={_$('服务数')} value={overviewData?.ServiceCount ?? '-'} />
            ) : null}
            <MetricCard label={_$('会话数')} value={overviewData?.ActiveSessionCount ?? '-'} />
            <MetricCard
              label={_$('Token消耗数')}
              value={
                <>
                  {tokenUsage.value}
                  {tokenUsage?.unit ? (
                    <span className="metric-card__suffix">{tokenUsage.unit}</span>
                  ) : null}
                </>
              }
            />
            <MetricCard label={_$('调用数')} value={overviewData?.InvocationCount ?? '-'} />
            <MetricCard
              label={_$('错误数')}
              value={overviewData?.ErrorCount ?? '-'}
              hasError={hasErrorCount}
            />
            <MetricCard
              label={_$('错误率')}
              hasError={hasErrorCount}
              value={
                <>
                  {overviewData?.ErrorRate !== null
                    ? numberFormat(overviewData?.ErrorRate * 100)
                    : '-'}
                  <span className="metric-card__suffix">%</span>
                </>
              }
            />
          </div>
        )}
      </section>
    </div>
  )
}

export default styled(Overview)`
  margin-top: 24px;
  .overview-metrics__grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 12px;

    .metric-card {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 16px 0;
      border-radius: 4px;
      background: linear-gradient(96.09deg, #fafdff 0%, #f1fbff 98.58%);
      border: 1px solid #e6f7ff;

      .metric-card__header {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 8px;

        .metric-card__label {
          font-size: 12px;
          font-weight: 400;
          line-height: 18px;
        }
      }

      .metric-card__value {
        font-size: 30px;
        font-weight: 600;
        line-height: 30px;
        font-family: 'DIN Alternate';

        .metric-card__suffix {
          margin-left: 4px;
          font-size: 14px;
          font-weight: 500;
          line-height: 1;
          color: ${variable.descColor};
        }
      }

      &.metric-card-error {
        background: linear-gradient(96.09deg, #fffcfc 0%, #fff1f1 98.58%);
        border: 1px solid #fff1f1;
        .metric-card__label,
        .metric-card__value,
        .metric-card__suffix {
          color: ${variable.errorColor};
        }
      }
    }
  }
`
